'use client';

import { useClerk } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { stripLocalePrefix } from '~/i18n/i18n-config';
import { logError } from '~/utils';
import { currentRelativeUrl } from '~/utils/urlHelpers';

// Routes proxy.ts gates, or that render nothing useful signed out — signing out of one has
// to leave it. Everywhere else the user keeps their place.
const GATED_PREFIXES = ['/account', '/_internal'];

/**
 * Signs out via a full document load rather than Clerk's soft navigation.
 *
 * Loyalty pricing is resolved server-side — the tier is an input to the engine call, so the
 * client never holds an untiered price to re-derive. Clerk refreshes the current route on
 * sign-out but skips its router-cache invalidation (`intent === 'sign-out'` short-circuits
 * on Next 15/16), and `staleTimes.dynamic` is 30s, so other routes visited while signed in
 * would keep serving club prices from cache. A document load is the only thing that clears
 * them, and sign-out is precisely when discarding client state is what you want.
 */
export function useSignOut() {
  const { signOut } = useClerk();
  const pathname = usePathname();

  return () => {
    const path = stripLocalePrefix(pathname ?? '/');
    const isGated = GATED_PREFIXES.some((prefix) => path.startsWith(prefix));
    // Query included: pages like /map/build keep their whole identity there.
    const target = isGated ? '/' : currentRelativeUrl(pathname ?? '/');

    // The callback form suppresses Clerk's own redirect so nothing races the document load,
    // but a click that silently does nothing is the worst outcome — so navigate from the
    // resolved promise too, whichever fires first. On failure the user is still signed in,
    // so deliberately don't navigate; log it rather than pretending it worked.
    let navigated = false;
    const leave = () => {
      if (navigated) return;
      navigated = true;
      globalThis.location.assign(target);
    };

    signOut(leave).then(leave).catch(logError);
  };
}
